import { Close } from '@mui/icons-material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SignaturePad from 'react-signature-canvas';
import './css/signOffComponent.css';
import { nullish } from '../../helpers/numbers';
import { isArray } from '../../helpers/typeOf';

const StyledSignaturePad = styled(SignaturePad)(() => ({
  position: 'relative',
  border: '1px solid rgba(0,0,0,0.2)',
  borderRadius: '4px',
}));

const StyledSignatureContainer = styled(Stack)(() => ({
  position: 'relative',
  border: '1px solid rgba(0,0,0,0.2)',
  borderRadius: '5px',
  height: '211px',
  padding: '0 5px 0 5px',
}));

const StyledClearIcon = styled(DeleteOutlinedIcon)(() => ({
  position: 'absolute',
  color: 'rgba(0,0,0,0.5)',
  cursor: 'pointer',
  top: '-26px',
  right: '0',
}));

const StyledSignActions = styled(Stack)(() => ({
  width: 'calc(100% - 15px)',
  position: 'absolute',
  top: '30px',
  borderBottom: '1px solid rgba(0,0,0,0.2)',
}));

export default function WOSignOffComponent({
  signOffData,
  isOpen,
  onClose,
  onSave,
  isLoading,
  amplifyApi,
  onChangeLoading = () => {},
  uploadUrl = '/uploads/reconditions/estimates/photos',
}) {
  const { t } = useTranslation('global');
  const [canvasImageUrl, setCanvasImageUrl] = useState();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [notes, setNotes] = useState('');
  const signaturePadRef = useRef();

  const setFilesAndEmitChange = newFiles => {
    onChangeLoading(false);
    setFiles(newFiles);
  };

  const handleClose = () => {
    if (signaturePadRef?.current?.clear) {
      signaturePadRef?.current?.clear();
    }
    setCanvasImageUrl(null);
    onClose();
  };

  const clearSignature = () => {
    signaturePadRef.current.clear();
    setCanvasImageUrl(null);
  };

  const handleSignatureChange = () => {
    setCanvasImageUrl(
      signaturePadRef.current.getTrimmedCanvas().toDataURL('image/png')
    );
  };

  const handleNameChange = e => {
    setName(e.target.value);
  };

  const handleLastNameChange = e => {
    setLastName(e.target.value);
  };

  const handleNotesChange = e => {
    setNotes(e.target.value);
  };

  const detailsByStatus = {
    uploading: () => 'Loading...',
    error: () => 'Error',
    success: file => `${file.size / 1000} KB`,
  };

  const findFileInProcessedFiles = (filesProcessed, file) => {
    return (
      filesProcessed.find(
        fileProcessed => fileProcessed.originalFilename === file.fileName
      ) || {}
    );
  };

  const projectFileToModel = (file, status = 'uploading') => {
    return {
      fileId: file.fileId || file.name,
      fileName: file.fileName || file.name,
      status,
      type: file.fileName?.split('.').pop() || file.name?.split('.').pop(),
      details: detailsByStatus[status](file),
      size: file.size,
    };
  };

  const sendFilesToAPI = newFiles => {
    const projectedNewFiles = [
      ...files,
      ...newFiles.map(file => projectFileToModel(file)),
    ];
    setFiles(projectedNewFiles);
    const formDataFiles = new FormData();
    newFiles.forEach(file => formDataFiles.append('files', file));

    onChangeLoading(true);

    amplifyApi(formDataFiles, uploadUrl, 'post')
      .then(response => {
        const processedFiles = isArray(response) ? response : [response];
        const currentFiles = [
          ...projectedNewFiles.map(file =>
            projectFileToModel(
              {
                ...file,
                ...findFileInProcessedFiles(processedFiles, file),
              },
              'success'
            )
          ),
        ];
        setFilesAndEmitChange(currentFiles);
        onSave({ name, lastName, notes, canvasImageUrl, files: currentFiles });
        setLoading(false);
      })
      .catch(ex => {
        setFilesAndEmitChange([
          ...files,
          ...newFiles.map(file => projectFileToModel(file, 'error')),
        ]);
        setLoading(false);
      });
  };

  const convertToBlob = async base64 => {
    const res = await fetch(base64);
    const buf = await res.arrayBuffer();
    return new File([buf], 'signoff_signature.jpeg', {
      type: 'image/jpeg',
    });
  };

  const saveSignature = async () => {
    setLoading(true);
    let imageData = '';
    if (signaturePadRef?.current?.getTrimmedCanvas) {
      imageData = signaturePadRef?.current
        ?.getTrimmedCanvas()
        .toDataURL()
        .toString();
    }
    const result = await convertToBlob(imageData);
    sendFilesToAPI([result]);
  };

  useEffect(() => {
    if (signOffData) {
      setName(signOffData?.name);
      setLastName(signOffData?.lastName);
      setNotes(signOffData?.notes);
    }
  }, [signOffData]);

  return (
    <Dialog
      data-testid="sign-off-component"
      open={!!isOpen}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '496px',
          },
        },
      }}
      fullWidth
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose();
        }
      }}
    >
      <DialogTitle
        sx={{ background: theme => theme.palette.grey[50] }}
        component={Stack}
        direction="row"
        alignItems="center"
      >
        <Typography variant="subtitle2" flexGrow={1}>
          {t('signOff.title')}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 6,
            color: theme => theme.palette.grey[500],
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2 }}>
        <Stack gap={2} pt={3}>
          <Stack direction="row" gap={2}>
            <TextField
              fullWidth
              label={t('signOff.name')}
              size="small"
              inputProps={{ 'data-testid': 'name' }}
              autoComplete="off"
              value={name}
              required
              onChange={handleNameChange}
            />
            <TextField
              fullWidth
              size="small"
              label={t('signOff.lastName')}
              onFocus={event => event.target.select()}
              inputProps={{ 'data-testid': 'last-name' }}
              autoComplete="off"
              value={lastName}
              required
              onChange={handleLastNameChange}
            />
          </Stack>
          <Stack gap={1}>
            <Typography variant="subtitle1" flexGrow={1}>
              {t('signOff.notes')}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              size="small"
              value={notes}
              onChange={handleNotesChange}
              inputProps={{
                'data-testid': 'notes',
              }}
            />
            <Typography
              variant="caption"
              flexGrow={1}
              color={'rgba(0,0,0,0.6)'}
            >
              {t('signOff.notesInfo')}
            </Typography>
          </Stack>
          <Stack gap={1}>
            <Typography variant="subtitle1" flexGrow={1}>
              {t('signOff.sign')}
            </Typography>
            <StyledSignatureContainer>
              <StyledSignActions>
                <StyledClearIcon onClick={clearSignature} />
              </StyledSignActions>
              <StyledSignaturePad
                ref={ref => {
                  signaturePadRef.current = ref;
                }}
                canvasProps={{ className: 'signatureCanvas' }}
                onEnd={handleSignatureChange}
              />
            </StyledSignatureContainer>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Stack direction="row" width="100%" justifyContent="end" gap={2}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={false}
            sx={{
              color: 'black',
              borderColor: 'lightgray',
              '&:hover': {
                backgroundColor: 'lightgray',
                borderColor: 'lightgray',
              },
            }}
          >
            {t('signOff.cancel')}
          </Button>
          <LoadingButton
            color="primary"
            variant="contained"
            loading={isLoading || loading}
            disabled={
              nullish(name) || nullish(lastName) || nullish(canvasImageUrl)
            }
            onClick={saveSignature}
          >
            {t('signOff.save')}
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
